import type {
  BaseFormItem,
  FormItem,
  InputDataType,
  FormSection,
} from "@superblocksteam/shared";

import type { HelpContent } from "store/slices/apisV2/control-flow/types";

enum TabBehaviour {
  INPUT = "INPUT",
  INDENT = "INDENT",
}

enum EditorSize {
  COMPACT = "COMPACT",
  EXTENDED = "EXTENDED",
}

type ControlInputDataType = InputDataType & {
  size?: EditorSize;
  borderLess?: boolean;
  showLineNumbers?: boolean;
  noNewlines?: boolean;
  tabBehaviour?: TabBehaviour;
  enableSearch?: boolean;
  isExpression?: boolean;
  lineWrapping?: boolean;
};

export type ControlFlowFormItem = (
  | FormItem
  | ExpressionEditorListFormItem
  | ExpressionEditorFormItem
  | LocalVariablesListFormItem
  | DynamicVariablesListFormItem
  | NumberInputFormItem
  | UniqueListInputFormItem
) & {
  controlFlowTooltip?: HelpContent;
  apiPbPaths?: string[]; // Array of ApiPb paths that affect this form item
};

export enum ControlFlowFormComponentType {
  EXPRESSION_EDITOR_LIST = "EXPRESSION_EDITOR_LIST",
  EXPRESSION_EDITOR = "EXPRESSION_EDITOR",
  LOCAL_VARIABLES_LIST = "LOCAL_VARIABLES_LIST",
  VARIABLES_LIST = "VARIABLES_LIST",
  NUMBER_INPUT = "NUMBER_INPUT",
  UNIQUE_LIST_INPUT = "UNIQUE_LIST_INPUT",
}

export { FormComponentType } from "@superblocksteam/shared";

type ControlFlowBaseFormItem = BaseFormItem & {
  expectedValue?: string;
  exampleData?: any;
};

interface UniqueListInputFormItem extends ControlFlowBaseFormItem {
  componentType: ControlFlowFormComponentType.UNIQUE_LIST_INPUT;
  placeholder?: string;
  addLabel?: string;
  label: string;
  itemTypeText?: string;
  getAddedName?: (idx: number) => string;
  getShowConfirmOnDelete?: (idx: number) => boolean;
  confirmTitle?: string;
}

interface ExpressionEditorFormItem extends ControlFlowBaseFormItem {
  componentType: ControlFlowFormComponentType.EXPRESSION_EDITOR;
  placeholder?: string;
}

interface ExpressionEditorListFormItem extends ControlFlowBaseFormItem {
  componentType: ControlFlowFormComponentType.EXPRESSION_EDITOR_LIST;
  placeholder?: string;
  dataType?: ControlInputDataType;
  subtitle?: string;
  valuePath?: string;
  addLabel?: string;
  getShowConfirmOnDelete?: (idx: number) => boolean;
  confirmTitle?: string;
}

interface DynamicVariablesListFormItem extends ControlFlowBaseFormItem {
  componentType: ControlFlowFormComponentType.VARIABLES_LIST;
  placeholder?: string;
  dataType?: ControlInputDataType;
  subtitle?: string;
  addLabel?: string;
}

interface LocalVariablesListFormItem extends ControlFlowBaseFormItem {
  componentType: ControlFlowFormComponentType.LOCAL_VARIABLES_LIST;
  placeholder?: string;
  subHeading?: string;
  variables?: Record<string, string>;
  varNameOverrides?: Record<string, string>;
}

interface NumberInputFormItem extends ControlFlowBaseFormItem {
  componentType: ControlFlowFormComponentType.NUMBER_INPUT;
  placeholder?: string;
  subHeading?: string;
}

export interface ControlFlowFormTemplate {
  sections: ControlFlowFormSection[];
}

export type FormSectionMeta = Omit<FormSection, "items">;

export interface ControlFlowFormSection extends FormSectionMeta {
  items: ControlFlowFormItem[];
}
